// ─────────────────────────────────────────────────────────────────────────────
// Integracoes.jsx — Página Integrações (/integracoes), versão rica.
// Mesma linguagem visual das páginas de funcionalidade: hero escuro com "hub"
// animado, marquee de logos, navegação por categoria, painéis com tiles,
// bloco de API & Webhooks, pipeline "Como funciona", FAQ (schema) e CTA.
// SEO (title/description/keywords) e FAQ mantidos da versão anterior.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { C, font, mono } from "../tokens";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FaqSection from "../components/FaqSection";
import { applySeo } from "../lib/seo";
import { Reveal, TileIcon, logoClass } from "./FuncionalidadeDetalhe";
import "../styles/blog.css";

// ─── Dados ───────────────────────────────────────────────────────────────────
const CATS = [
  { id: "erps", title: "ERPs", icon: "shuffle", bg: "linear-gradient(135deg, #0560FF 0%, #204493 100%)", color: "#0560FF",
    desc: "Sincronize empreendimentos, unidades, contratos e financeiro com o seu ERP.",
    items: [["Sienge", "sienge"], ["UAU", "uau"], ["Informakon", "informakon"], ["TOTVS", "totvs"], ["Mega", "mega"], ["Conta Azul", "conta-azul"]] },
  { id: "marketing", title: "CRM & Marketing", icon: "inbox", bg: "linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)", color: "#7C3AED",
    desc: "Capture leads e meça campanhas com seus canais de aquisição favoritos.",
    items: [["HubSpot", "hubspot"], ["RD Station", "rd-station"], ["Facebook Ads", "facebook-ads"]] },
  { id: "assinatura", title: "Assinatura digital", icon: "pen", bg: "linear-gradient(135deg, #10B981 0%, #047857 100%)", color: "#10B981",
    desc: "Envie contratos para assinatura eletrônica com validade jurídica.",
    items: [["ClickSign", "clicksign"], ["DocuSign", "docusign"], ["ZapSign", "zapsign"], ["D4Sign", "d4sign"]] },
  { id: "portais", title: "Portais imobiliários", icon: "map", bg: "linear-gradient(135deg, #0EA5E9 0%, #0560FF 100%)", color: "#0EA5E9",
    desc: "Publique e atualize seu estoque nos principais portais do mercado.",
    items: [["ZAP Imóveis", "zap"], ["Viva Real", "viva-real"], ["ImovelWeb", "imovel-web"]] },
  { id: "atendimento", title: "Atendimento & IA", icon: "chat", bg: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)", color: "#128C7E",
    desc: "Atenda, qualifique e converta leads com automação e inteligência artificial.",
    items: [["WhatsApp", "whatsapp"], ["BotConversa", "bot-conversa"], ["Laís IA", "lais"], ["Morada IA", "morada"]] },
  { id: "bancos", title: "Bancos", icon: "money", bg: "linear-gradient(135deg, #F59E0B 0%, #B45309 100%)", color: "#D97706",
    desc: "Emissão de boletos, remessa e retorno e conciliação automática com mais de 30 bancos.",
    banks: [["Itaú", "itau.png"], ["Banco do Brasil", "bb.png"], ["Bradesco", "bradesco.png"], ["Santander", "santander.png"], ["Caixa", "caixa.png"], ["C6 Bank", "c6.png"], ["BTG Pactual", "btg.svg"], ["Banco Inter", "inter.svg"], ["Banco Safra", "safra.png"], ["Banco BRB", "brb.png"], ["Sicredi", "sicredi.png"], ["Sicoob", "sicoob.png"]] },
  { id: "dev", title: "Automação & Dev", icon: "bolt", bg: "linear-gradient(135deg, #0284C7 0%, #0560FF 100%)", color: "#0284C7",
    desc: "Conecte qualquer ferramenta com nossa API aberta, webhooks e automações.",
    items: [["Zapier", "zapier"], ["Webhooks", "webhook"], ["API REST", "api"]] },
];

const HUB_LOGOS = ["sienge", "hubspot", "clicksign", "zap", "whatsapp", "zapier", "uau", "rd-station", "docusign", "viva-real", "facebook-ads", "totvs"];

const FAQ = [
  { q: "Vocês fazem integração sob demanda?", a: "Sim. Além dos conectores nativos, nossa equipe avalia e desenvolve integrações específicas para o seu fluxo de trabalho. Conte qual ferramenta você usa e nós analisamos a melhor forma de conectar." },
  { q: "O IMOBMEET tem API e webhooks?", a: "Sim. Oferecemos uma API REST aberta e documentada, além de webhooks para eventos em tempo real — ideal para automações, integrações personalizadas e sincronização com seus sistemas internos." },
  { q: "Quais ERPs são suportados?", a: "Trabalhamos com os principais ERPs do mercado imobiliário, incluindo Sienge, UAU, Informakon, TOTVS e Mega. A sincronização cobre empreendimentos, unidades, contratos e dados financeiros." },
  { q: "Há custo adicional para integrar?", a: "As integrações nativas estão incluídas no seu plano. Integrações sob demanda ou desenvolvimentos personalizados podem ter um escopo dedicado — falaremos sobre isso na demonstração, sem surpresas." },
];

const STEPS = [
  { icon: "shuffle", title: "Escolha a integração", text: "ERP, marketing, assinatura, portais, WhatsApp, bancos ou API." },
  { icon: "shield", title: "Conecte a conta", text: "Credenciais ou chave de API da ferramenta que você já usa." },
  { icon: "map", title: "Mapeie os dados", text: "Empreendimentos, unidades, etapas e campos de cada lado." },
  { icon: "bolt", title: "Sincronize", text: "Leads, vendas, contratos e financeiro fluindo automaticamente." },
  { icon: "users", title: "Implantação acompanhada", text: "Nossa equipe valida a integração com você antes de ir ao ar." },
];

const Check = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// ─── Hub animado: IMOBMEET no centro, logos em órbita com pulsos de dados ─────
function IntegrationHub() {
  const n = HUB_LOGOS.length;
  return (
    <div className="ihub" aria-hidden="true">
      <div className="ihub-orbit" />
      <div className="ihub-orbit inner" />
      <div className="ihub-ring">
        {HUB_LOGOS.map((l, i) => {
          const deg = (360 / n) * i;
          return (
            <div key={l} className="ihub-arm" style={{ transform: `rotate(${deg}deg)` }}>
              <span className="ihub-line" />
              <i className="ihub-dot" style={{ animationDelay: `${(i * 0.45) % 3}s` }} />
              <span className="ihub-node">
                <span className="ihub-node-in" style={{ transform: `rotate(${-deg}deg)` }}>
                  <img src={`/assets/integ/${l}.png`} alt="" className={logoClass(l)} loading="eager" decoding="async" />
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <div className="ihub-center">
        <img src="/assets/icon-imobmeet.png" alt="IMOBMEET" />
      </div>
    </div>
  );
}

// ─── Payload de webhook animado ──────────────────────────────────────────────
const CODE_LINES = [
  ["POST", " /webhooks/imobmeet"],
  ["{"],
  ['  "event": ', '"lead.created"', ","],
  ['  "lead": { "nome": ', '"Ana Paula"', ', "origem": ', '"Meta Ads"', " },"],
  ['  "empreendimento": ', '"Vista 21"', ","],
  ['  "responsavel": ', '"Marcos R."'],
  ["}"],
];
function WebhookCard() {
  return (
    <div className="feat-window" style={{ background: "#0B1224" }}>
      <div className="feat-window-bar" style={{ justifyContent: "space-between" }}>
        <span style={{ display: "flex", gap: 6 }}>
          <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
        </span>
        <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>Webhook · evento em tempo real</span>
        <span style={{ width: 40 }} />
      </div>
      <pre className="wh-code" style={{ fontFamily: mono }}>
        {CODE_LINES.map((parts, i) => (
          <span key={i} className="wh-line" style={{ animationDelay: `${0.3 + i * 0.35}s` }}>
            {parts.map((p, j) => {
              const isStr = p.startsWith('"') && p.endsWith('"') && p.length > 1 && j > 0 && !p.includes(":");
              const isMethod = i === 0 && j === 0;
              return <span key={j} className={isMethod ? "wh-method" : isStr ? "wh-str" : ""}>{p}</span>;
            })}
          </span>
        ))}
        <span className="wh-cursor" />
      </pre>
      <div className="wh-badge" style={{ fontFamily: font }}>
        <span className="dot" /> 200 OK · entregue
      </div>
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────
export default function Integracoes() {
  useEffect(() => {
    applySeo({
      title: "Integrações: Sienge, UAU, RD Station, ZAP e mais | IMOBMEET",
      description: "Integre o IMOBMEET ao seu stack: ERPs (Sienge, UAU, Informakon, TOTVS), marketing (RD Station, Meta Ads), assinatura digital, portais e +30 bancos.",
      keywords: "integração Sienge, integração UAU, integração RD Station, integração ZAP, CRM imobiliário integrações, integração ERP imobiliário, API imobiliária",
      path: "/integracoes",
    });
    window.scrollTo(0, 0);
  }, []);

  const total = CATS.reduce((a, c) => a + (c.items ? c.items.length : c.banks.length), 0);

  return (
    <>
      <Navbar activeSolution="/integracoes" />

      {/* ── Hero ── */}
      <section className="feat-hero" style={{ background: "linear-gradient(135deg, #0B1733 0%, #0A1230 100%)", padding: "104px 28px 72px" }}>
        <div className="feat-gridanim" />
        <div className="feat-orb" style={{ width: 520, height: 520, background: C.accent, top: -200, left: -160 }} />
        <div className="feat-orb b" style={{ width: 460, height: 460, background: "#10B981", bottom: -220, right: -120, opacity: .35 }} />
        <div className="integ-hero" style={{ maxWidth: 1160, margin: "0 auto", position: "relative" }}>
          <div>
            <div className="feat-rise" style={{ animationDelay: "0.05s" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: font, fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #0560FF 0%, #204493 100%)", padding: "6px 14px", borderRadius: 999, marginBottom: 22, boxShadow: "0 10px 30px rgba(5,96,255,.35)" }}>
                Integrações nativas · API aberta
              </span>
            </div>
            <h1 className="feat-rise" style={{ fontFamily: font, fontSize: "clamp(34px, 4.8vw, 54px)", fontWeight: 800, lineHeight: 1.04, letterSpacing: -1.5, color: "#fff", margin: "0 0 18px", animationDelay: "0.15s" }}>
              Conecte o IMOBMEET com <span style={{ background: "linear-gradient(90deg, #5B9CFF, #7DD3FC)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>todo o seu stack</span>
            </h1>
            <p className="feat-rise" style={{ fontFamily: font, fontSize: 18, lineHeight: 1.6, color: "rgba(255,255,255,0.82)", margin: "0 0 30px", maxWidth: 560, animationDelay: "0.25s" }}>
              ERPs, marketing, assinatura digital, portais imobiliários, WhatsApp e bancos. Coloque sua operação para conversar sem retrabalho — e o que não existir, a gente integra pra você.
            </p>
            <div className="feat-rise" style={{ display: "flex", gap: 12, flexWrap: "wrap", animationDelay: "0.35s" }}>
              <Link to="/agendar-demo" style={{ fontFamily: font, fontSize: 15, fontWeight: 800, color: "#fff", background: C.accent, padding: "14px 26px", borderRadius: 12, textDecoration: "none", boxShadow: "0 10px 28px rgba(5,96,255,0.45)" }}>
                Agendar demonstração →
              </Link>
              <a href="#categorias" style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: "#fff", background: "rgba(255,255,255,0.08)", border: "1.5px solid rgba(255,255,255,0.26)", padding: "14px 26px", borderRadius: 12, textDecoration: "none", backdropFilter: "blur(6px)" }}>
                Ver todas as integrações
              </a>
            </div>
            <div className="feat-rise" style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 34, animationDelay: "0.45s" }}>
              {[[`${total}+`, "integrações nativas"], ["7", "categorias"], ["API", "REST + webhooks"]].map(([v, l]) => (
                <div key={l} style={{ fontFamily: font }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: "#fff", letterSpacing: -0.6 }}>{v}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: 0.6 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="feat-rise" style={{ animationDelay: "0.3s" }}>
            <IntegrationHub />
          </div>
        </div>
      </section>

      {/* ── Categorias ── */}
      <section id="categorias" style={{ background: C.surface, padding: "72px 28px 40px", scrollMarginTop: 80 }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <Reveal style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 26px" }}>
            <h2 style={{ fontFamily: font, fontSize: "clamp(26px,3.4vw,38px)", fontWeight: 800, letterSpacing: -1, color: C.text, margin: "0 0 12px" }}>
              Integrações por categoria
            </h2>
            <p style={{ fontFamily: font, fontSize: 17, lineHeight: 1.65, color: C.muted, margin: 0 }}>
              Conecte cada parte da sua jornada de vendas e gestão. Tudo centralizado no IMOBMEET.
            </p>
          </Reveal>

          {/* navegação fixa por categoria */}
          <nav className="integ-nav" aria-label="Categorias de integração">
            {CATS.map((c) => (
              <a key={c.id} href={`#cat-${c.id}`} className="integ-chip" style={{ fontFamily: font }}>
                <span className="ic" style={{ background: c.bg }}><TileIcon name={c.icon} /></span>
                {c.title}
              </a>
            ))}
          </nav>

          <div style={{ display: "flex", flexDirection: "column", gap: 22, marginTop: 26 }}>
            {CATS.map((c, i) => (
              <Reveal key={c.id} delay={Math.min((i % 3) + 1, 3)} className="integ-cat" id={`cat-${c.id}`}>
                <div className="integ-cat-head">
                  <span className="integ-cat-icon" style={{ background: c.bg, boxShadow: `0 12px 28px ${c.color}55` }}>
                    <TileIcon name={c.icon} />
                  </span>
                  <h3 style={{ fontFamily: font, fontSize: 22, fontWeight: 800, letterSpacing: -0.5, color: C.text, margin: "14px 0 6px" }}>{c.title}</h3>
                  <p style={{ fontFamily: font, fontSize: 14.5, lineHeight: 1.6, color: C.muted, margin: 0 }}>{c.desc}</p>
                  <span style={{ display: "inline-block", marginTop: 12, fontFamily: font, fontSize: 12, fontWeight: 800, color: c.color, letterSpacing: 0.5, textTransform: "uppercase" }}>
                    {(c.items ? c.items.length : c.banks.length)} {c.banks ? "bancos" : "integrações"}
                  </span>
                </div>
                <div className="integ-tiles">
                  {c.items && c.items.map(([name, logo]) => (
                    <div key={name} className="feat-card integ-tile">
                      <span className="feat-tile-bar" style={{ background: c.bg }} />
                      <img src={`/assets/integ/${logo}.png`} alt={name} className={logoClass(logo)} loading="lazy" decoding="async" />
                      <span style={{ fontFamily: font }}>{name}</span>
                    </div>
                  ))}
                  {c.banks && c.banks.map(([name, file]) => (
                    <div key={name} className="feat-card integ-tile bank">
                      <span className="feat-tile-bar" style={{ background: c.bg }} />
                      <img src={`/assets/banks/${file}`} alt={name} loading="lazy" decoding="async" />
                      <span style={{ fontFamily: font }}>{name}</span>
                    </div>
                  ))}
                  {c.banks && (
                    <div className="integ-tile more" style={{ background: c.bg, fontFamily: font }}>+30 Bancos disponíveis</div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── API & Webhooks ── */}
      <section style={{ background: C.bgAlt, padding: "76px 28px" }}>
        <div className="feat-block" style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56 }}>
          <Reveal>
            <span style={{ display: "inline-block", fontFamily: font, fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: "#0284C7", marginBottom: 12 }}>API & WEBHOOKS</span>
            <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,32px)", fontWeight: 800, letterSpacing: -0.8, color: C.text, margin: "0 0 14px", lineHeight: 1.12 }}>
              Não achou o conector? Use a API aberta
            </h2>
            <p style={{ fontFamily: font, fontSize: 16.5, lineHeight: 1.7, color: "#3A4A63", margin: "0 0 18px" }}>
              API REST documentada e webhooks para eventos em tempo real: lead criado, etapa alterada, proposta aprovada, venda fechada. Conecte seus sistemas internos, o Zapier ou qualquer ferramenta.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {["API REST aberta e documentada", "Webhooks por evento, em tempo real", "Zapier para automações sem código", "Integração sob demanda com a nossa equipe"].map((b) => (
                <div key={b} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <Check />
                  <span style={{ fontFamily: font, fontSize: 15, lineHeight: 1.5, color: C.text, fontWeight: 500 }}>{b}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={2} className="feat-media">
            <div className="feat-stage">
              <div className="feat-glow" style={{ background: "radial-gradient(60% 60% at 50% 50%, #0284C7 0%, #0560FF 55%, transparent 100%)" }} />
              <div className="feat-tilt"><WebhookCard /></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Como funciona ── */}
      <section style={{ background: C.surface, padding: "56px 28px 84px" }}>
        <Reveal className="feat-pipe-wrap" style={{ maxWidth: 1160, margin: "0 auto" }}>
          <div className="feat-gridanim" />
          <div className="feat-orb" style={{ width: 420, height: 420, background: C.accent, top: -200, left: -120, opacity: .45 }} />
          <div className="feat-orb b" style={{ width: 380, height: 380, background: "#10B981", bottom: -220, right: -100, opacity: .35 }} />
          <div style={{ position: "relative", textAlign: "center", marginBottom: 44 }}>
            <span style={{ display: "inline-block", fontFamily: font, fontSize: 11.5, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "#9DBBFF", background: "rgba(5,96,255,0.18)", border: "1px solid rgba(157,187,255,0.25)", padding: "6px 12px", borderRadius: 999, marginBottom: 14 }}>
              Passo a passo
            </span>
            <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,34px)", fontWeight: 800, letterSpacing: -0.8, color: "#fff", margin: 0 }}>
              Como funciona a integração
            </h2>
          </div>
          <div className="feat-pipe dark" style={{ gridTemplateColumns: `repeat(${STEPS.length}, 1fr)` }}>
            <div className="feat-pipe-line" style={{ left: `${100 / (2 * STEPS.length)}%`, right: `${100 / (2 * STEPS.length)}%` }}>
              <span className="feat-pipe-glow" />
            </div>
            {STEPS.map((s, i) => (
              <div key={i} className="feat-pipe-card" style={{ textAlign: "center" }}>
                <div className="feat-pipe-dot" style={{ background: "linear-gradient(135deg, #0560FF 0%, #204493 100%)", fontFamily: font, animationDelay: `${i * 0.45}s` }}>{i + 1}</div>
                <div className="feat-glass">
                  <span className="feat-glass-num" style={{ fontFamily: font }}>{String(i + 1).padStart(2, "0")}</span>
                  <span style={{ width: 44, height: 44, borderRadius: 13, background: "linear-gradient(135deg, #0560FF 0%, #204493 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14, boxShadow: "0 10px 26px rgba(5,96,255,.4)" }}>
                    <TileIcon name={s.icon} />
                  </span>
                  <h3 style={{ fontFamily: font, fontSize: 16, fontWeight: 800, color: "#fff", margin: "0 0 6px", lineHeight: 1.25 }}>{s.title}</h3>
                  <p style={{ fontFamily: font, fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.72)", margin: 0 }}>{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ── FAQ (schema FAQPage) ── */}
      <FaqSection items={FAQ} title="Dúvidas sobre integrações" />

      {/* ── CTA ── */}
      <section style={{ background: C.bgAlt, padding: "0 28px 72px" }}>
        <Reveal className="feat-cta" style={{ maxWidth: 1160, margin: "0 auto", padding: "50px 40px", borderRadius: 24, background: "linear-gradient(135deg, #0560FF 0%, #204493 100%)", color: "#fff", textAlign: "center" }}>
          <div className="feat-orb" style={{ width: 320, height: 320, background: "#10B981", top: -160, right: -80, opacity: .4 }} />
          <div style={{ position: "relative" }}>
            <h2 style={{ fontFamily: font, fontSize: "clamp(24px,3vw,30px)", fontWeight: 800, letterSpacing: -0.7, margin: "0 0 12px" }}>
              Não encontrou a integração que buscava? A gente integra pra você.
            </h2>
            <p style={{ fontFamily: font, fontSize: 16.5, lineHeight: 1.6, color: "rgba(255,255,255,0.85)", margin: "0 auto 26px", maxWidth: 600 }}>
              Agende uma demonstração e veja como o IMOBMEET organiza e automatiza a operação da sua incorporadora ou loteadora.
            </p>
            <Link to="/agendar-demo" className="feat-card" style={{ display: "inline-flex", fontFamily: font, fontSize: 15, fontWeight: 800, color: C.accent, background: "#fff", padding: "15px 32px", borderRadius: 12, textDecoration: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              Agendar demonstração →
            </Link>
          </div>
        </Reveal>
      </section>

      <Footer />
    </>
  );
}
